import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "design-system/atoms/Button"
import { SheetFooter } from "design-system/atoms/Sheet"
import { Form } from "design-system/organisms/Form"
import { getErrorTranslations } from "lib/i18n"
import { useKindBadge } from "modules/capabilities/hooks/useKindBadge"
import { useCallback, useMemo, useState } from "react"
import { useForm, UseFormReturn } from "react-hook-form"
import { useTranslation } from "react-i18next"
import {
  CapabilityCampaignAssignation,
  CapabilityIdentifier,
  ConversationFlowEnum,
  GeneratedRequirement,
  InstructionCapabilityConfigSchema,
  McpCapabilityConfigSchema,
  WebhookBodyFormat,
  WebhookCapabilityConfigSchema,
} from "schema"
import { toast } from "sonner"
import z from "zod"
import { useInstallCapabilityMutation } from "../../hooks/useInstallCapabilityMutation"
import { CapabilityActivationSettings } from "../CapabilityActivationSettings"
import { CapabilitySheetLayout } from "../CapabilitySheetLayout"
import { InstructionCapabilityForm } from "./InstructionCapabilityForm"
import MCPCapabilityForm from "./MCPCapabilityForm"
import { WebhookCapabilityForm } from "./WebhookCapabilityForm"

type Props = {
  onOpenChange: (open: boolean) => void
  kind: "webhook" | "instruction" | "mcp"
}

function getDefaultValues(kind: Props["kind"]) {
  switch (kind) {
    case "instruction":
      return {
        name: "",
        activationPrompt: "",
        dataToExtract: [],
        agentPrompt: "",
        executeOnlyOnce: false,
      }

    case "webhook":
      return {
        name: "",
        url: "",
        method: "GET" as const,
        activationPrompt: "",
        dataToExtract: [],
        webhookHeaders: [],
        webhookBody: "{}",
        webhookBodyFormat: WebhookBodyFormat.json,
        agentPrompt: "",
        useCache: true,
        testResponseBody: null,
        executeOnlyOnce: false,
        mode: "single" as const,
        steps: [],
      }

    case "mcp":
      return {
        name: "",
        transportType: "streamable-http" as const,
        serverUrl: "",
        authConfig: {
          type: "none" as const,
        },
        tools: [],
        connectionTimeoutMs: "10000",
        executionTimeoutMs: "15000",
      }
  }
}

export function CreateCustomCapability({ onOpenChange, kind }: Props) {
  const { t } = useTranslation()
  const [campaignAssignation, setCampaignAssignation] = useState<CapabilityCampaignAssignation>(
    CapabilityCampaignAssignation.AllCampaigns,
  )
  const [campaignFlow, setCampaignFlow] = useState<keyof typeof ConversationFlowEnum>(
    ConversationFlowEnum.Inbound,
  )
  const [campaignIds, setCampaignIds] = useState<string[]>([])
  const [instructionRequirements, setInstructionRequirements] = useState<GeneratedRequirement[]>([])
  const [requirementsAgentPrompt, setRequirementsAgentPrompt] = useState<string | null>(null)
  const { mutateAsync: installCapability, isPending: isInstallCapabilityPending } =
    useInstallCapabilityMutation()
  const getBadge = useKindBadge()

  const selectedIdentifier = useMemo<CapabilityIdentifier>(() => {
    switch (kind) {
      case "webhook":
        return CapabilityIdentifier.Webhook
      case "instruction":
        return CapabilityIdentifier.Instruction
      case "mcp":
        return CapabilityIdentifier.MCP
    }
  }, [kind])

  const schema = useMemo(() => {
    switch (kind) {
      case "webhook":
        return WebhookCapabilityConfigSchema
      case "instruction":
        return InstructionCapabilityConfigSchema
      case "mcp":
        return McpCapabilityConfigSchema
    }
  }, [kind])

  const form = useForm<
    | z.infer<typeof WebhookCapabilityConfigSchema>
    | z.infer<typeof InstructionCapabilityConfigSchema>
    | z.infer<typeof McpCapabilityConfigSchema>
  >({
    resolver: zodResolver(schema),
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    defaultValues: getDefaultValues(kind),
  })

  const onClose = useCallback(() => {
    onOpenChange(false)
  }, [onOpenChange])

  const handleFormSubmit = useCallback(
    async (values: z.infer<typeof schema>) => {
      if (isInstallCapabilityPending) return

      let config: z.infer<typeof schema>

      switch (kind) {
        case "webhook":
          const webhookValues = values as z.infer<typeof WebhookCapabilityConfigSchema>
          config = {
            name: webhookValues.name.trim(),
            method: webhookValues.method,
            activationPrompt: webhookValues.activationPrompt.trim(),
            dataToExtract: webhookValues.dataToExtract,
            url: webhookValues.url?.trim() || undefined,
            webhookHeaders: webhookValues.webhookHeaders,
            webhookBody: webhookValues.webhookBody,
            webhookBodyFormat: webhookValues.webhookBodyFormat,
            agentPrompt: webhookValues.agentPrompt.trim(),
            useCache: webhookValues.useCache,
            testResponseBody: webhookValues.testResponseBody,
            executeOnlyOnce: webhookValues.executeOnlyOnce,
            mode: webhookValues.mode,
            steps: webhookValues.steps,
          } as z.infer<typeof schema>
          break

        case "instruction":
          const instructionValues = values as z.infer<typeof InstructionCapabilityConfigSchema>
          config = {
            name: instructionValues.name.trim(),
            activationPrompt: instructionValues.activationPrompt.trim(),
            dataToExtract: instructionValues.dataToExtract,
            agentPrompt: instructionValues.agentPrompt.trim(),
            executeOnlyOnce: instructionValues.executeOnlyOnce,
          } as z.infer<typeof schema>
          break

        case "mcp":
          const mcpValues = values as z.infer<typeof McpCapabilityConfigSchema>
          config = {
            name: mcpValues.name.trim(),
            transportType: mcpValues.transportType,
            serverUrl: mcpValues.serverUrl.trim(),
            authConfig: mcpValues.authConfig,
            tools: mcpValues.tools,
            connectionTimeoutMs: mcpValues.connectionTimeoutMs,
            executionTimeoutMs: mcpValues.executionTimeoutMs,
          } as z.infer<typeof schema>
          break
      }

      const requirementsToSave = getInstructionRequirementsForSave({
        kind,
        config,
        requirements: instructionRequirements,
        requirementsAgentPrompt,
      })

      const result = await installCapability({
        identifier: selectedIdentifier,
        config,
        campaignAssignation,
        campaignIds,
        campaignFlow,
        ...(requirementsToSave ? { requirements: requirementsToSave } : {}),
      })

      if (result) {
        toast.success(
          t("capabilities.list.install.success", {
            capability: t(`capabilities.catalog.${selectedIdentifier}.name`),
          }),
        )

        onClose()
      }
    },
    [
      selectedIdentifier,
      isInstallCapabilityPending,
      installCapability,
      campaignAssignation,
      campaignIds,
      campaignFlow,
      t,
      onClose,
      kind,
      instructionRequirements,
      requirementsAgentPrompt,
    ],
  )

  const renderContent = () => {
    const activationSettings = (
      <CapabilityActivationSettings
        campaignAssignation={campaignAssignation}
        setCampaignAssignation={setCampaignAssignation}
        campaignIds={campaignIds}
        setCampaignIds={setCampaignIds}
        campaignFlow={campaignFlow}
        setCampaignFlow={setCampaignFlow}
      />
    )

    switch (kind) {
      case "webhook":
        return (
          <WebhookCapabilityForm
            form={form as UseFormReturn<z.infer<typeof WebhookCapabilityConfigSchema>>}
            activationSettings={activationSettings}
          />
        )

      case "instruction":
        return (
          <InstructionCapabilityForm
            form={form as UseFormReturn<z.infer<typeof InstructionCapabilityConfigSchema>>}
            activationSettings={activationSettings}
            requirements={instructionRequirements}
            requirementsAgentPrompt={requirementsAgentPrompt}
            onRequirementsChange={setInstructionRequirements}
            onRequirementsAgentPromptChange={setRequirementsAgentPrompt}
          />
        )

      case "mcp":
        return (
          <MCPCapabilityForm
            form={form as UseFormReturn<z.infer<typeof McpCapabilityConfigSchema>>}
            activationSettings={activationSettings}
            isLoading={isInstallCapabilityPending}
          />
        )
    }
  }

  const dialogTitle = useMemo(() => {
    switch (kind) {
      case "webhook":
        return t("capabilities.webhookConfig.title")
      case "instruction":
        return t("capabilities.instructionConfig.title")
      case "mcp":
        return t("capabilities.mcpConfig.title")
    }
  }, [kind, t])

  const dialogDescription = useMemo(() => {
    switch (kind) {
      case "webhook":
        return t("capabilities.webhookConfig.description")
      case "instruction":
        return t("capabilities.instructionConfig.description")
      case "mcp":
        return t("capabilities.mcpConfig.description")
    }
  }, [kind, t])

  const badge = useMemo(() => {
    switch (kind) {
      case "instruction":
        return getBadge("Instruction")
      case "webhook":
        return getBadge("Webhook")
      case "mcp":
        return getBadge("MCP")
    }
  }, [getBadge, kind])

  return (
    <CapabilitySheetLayout
      open
      onOpenChange={onOpenChange}
      badge={badge}
      title={dialogTitle}
      description={dialogDescription}
    >
      <Form
        {...form}
        onSubmit={form.handleSubmit(handleFormSubmit)}
        errorTranslations={getErrorTranslations()}
        className="flex flex-col flex-1 min-h-0"
      >
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
          {renderContent()}
        </div>
        <SheetFooter className="flex shrink-0 flex-row justify-between gap-2 border-t bg-background p-5">
          <Button type="button" variant="outline" onClick={onClose}>
            {t("shared.cancel")}
          </Button>
          <Button type="submit" disabled={isInstallCapabilityPending}>
            {t("shared.create")}
          </Button>
        </SheetFooter>
      </Form>
    </CapabilitySheetLayout>
  )
}

function getInstructionRequirementsForSave({
  kind,
  config,
  requirements,
  requirementsAgentPrompt,
}: {
  kind: Props["kind"]
  config: unknown
  requirements: GeneratedRequirement[]
  requirementsAgentPrompt: string | null
}) {
  if (kind !== "instruction" || requirements.length === 0) return undefined

  const parsed = InstructionCapabilityConfigSchema.safeParse(config)
  if (!parsed.success || requirementsAgentPrompt !== parsed.data.agentPrompt) return undefined

  return requirements
}
